import { createShallowStore, createStoreWithPersistAndSubscribe } from '@frontend/store';
import { Verticals } from '../utils/constants';
import type { ColumnFilterValues } from '../utils/typings';

interface IntegrationsDashboardState {
  isUserAuthenticated: boolean;
  isUserLoggingIn: boolean;
  isUserRefreshing: boolean;
  // Map of migration id -> `migratedAt` timestamp. Hydrated from the
  // ListMigrations response because GetMigrationByID does not return this field.
  migratedAtById: Record<string, string | undefined>;
  dashboardSelectedVertical: Verticals;
  dashboardSearchQuery: string;
  dashboardColumnFilters: ColumnFilterValues;
}

interface IntegrationsDashboardActions {
  setIsUserAuthenticated: (isAuthenticated: boolean) => void;
  setIsUserLoggingIn: (isClicked: boolean) => void;
  setIsUserRefreshing: (isRefreshing: boolean) => void;
  setMigratedAtEntries: (entries: Array<{ id?: string; migratedAt?: string }>) => void;
  setDashboardSelectedVertical: (vertical: Verticals) => void;
  setDashboardSearchQuery: (query: string) => void;
  setDashboardColumnFilters: (filters: ColumnFilterValues) => void;
  resetState: () => void;
}

type IntegrationsDashboardStore = IntegrationsDashboardState & IntegrationsDashboardActions;

const initialState: IntegrationsDashboardState = {
  isUserAuthenticated: false, //True if user is authenticated, false otherwise.
  isUserLoggingIn: false, //True when login process starts, false when it ends.
  isUserRefreshing: false, //True when user triggers a data refresh, false when it ends.
  migratedAtById: {},
  dashboardSelectedVertical: Verticals.ALL,
  dashboardSearchQuery: '',
  dashboardColumnFilters: {},
};

export const useIntegrationsDashboardStore = createStoreWithPersistAndSubscribe<IntegrationsDashboardStore>(
  (set) => ({
    ...initialState,
    setIsUserAuthenticated: (isAuthenticated: boolean) => {
      set({ isUserAuthenticated: isAuthenticated });
    },
    setIsUserLoggingIn: (isClicked: boolean) => {
      set({ isUserLoggingIn: isClicked });
    },
    setIsUserRefreshing: (isRefreshing: boolean) => {
      set({ isUserRefreshing: isRefreshing });
    },
    setMigratedAtEntries: (entries) => {
      set((state) => {
        const next = { ...state.migratedAtById };
        let changed = false;
        for (const { id, migratedAt } of entries) {
          if (!id) continue;
          if (next[id] !== migratedAt) {
            next[id] = migratedAt;
            changed = true;
          }
        }
        return changed ? { migratedAtById: next } : state;
      });
    },
    setDashboardSelectedVertical: (vertical: Verticals) => {
      set({ dashboardSelectedVertical: vertical });
    },
    setDashboardSearchQuery: (query: string) => {
      set({ dashboardSearchQuery: query });
    },
    setDashboardColumnFilters: (filters: ColumnFilterValues) => {
      set({ dashboardColumnFilters: filters });
    },
    resetState: () => {
      set(initialState);
    },
  }),
  {
    name: 'IntegrationsDashboard',
  },
  {
    name: 'IntegrationsDashboardStore',
    enabled: true,
  }
);

export const useIntegrationsDashboardShallowStore =
  createShallowStore<IntegrationsDashboardStore>(useIntegrationsDashboardStore);
